import useContent from '../stores/useContent';
import expandUploadPath from '../lib/expandUploadPath';
import safeLocalStorage from './safeLocalStorage';
import SCORM from './SCORM';

// whether sfx is enabled in this course
let isEnabled = true;

// object to store SFX audio audioElements
const audioElements = {};

// disable if saved in suspend data
if (SCORM.get('disableSfx')) {
  disableSfx();
}

function getSfxPath(id: string) {
  const content = useContent();
  const sfxValue = content.compiled?.course?.sfx;
  const sfxFolder = sfxValue === true ? 'default' : sfxValue;

  if (!sfxFolder) return false;

  const path = `uploads/sfx/${sfxFolder}/${id}.mp3`;
  return path;
}

/**
 * Returns true if the user has muted the course or if the browser tab is
 * hidden.
 */
function isSilent() {
  // don't play in hidden browser tab
  const isHidden = 'hidden' in document && document.hidden;

  return !isEnabled || isHidden;
}

function disableSfx() {
  isEnabled = false;
  SCORM.set('disableSfx', true);
}

function enableSfx() {
  isEnabled = true;
  SCORM.set('disableSfx', false);
}

function getFile(id: string) {
  if (!id) return null;

  const themedPath = getSfxPath(id);
  if (!themedPath) return false;
  const src = expandUploadPath(themedPath);

  /**
   * For audio we have previously requested we want to use the stored audio element
   * unless it had an error, then we remove it and request it again
   * if we don't remove the failed references and load again we don't get errors for missing audio
   */
  if (src in audioElements) {
    const file = audioElements[src];
    if (file.readyState === 4) {
      return file;
    } else if (file.error && file.error.code === 4) {
      file.src = null;
      audioElements[src] = null;
    }
  }

  const file = new Audio(src);
  file.preload = 'auto';
  file.crossOrigin = 'anonymous';
  // save a ref to the audio element to use for future calls for that audio ID
  audioElements[src] = file;

  return file;
}

/*
 * Used by sfc to preload sound effects files
 */
function preloadSfx(id?: string) {
  if (id) getFile(id);
}

/**
 * Plays an SFX audio file concurrently with any other audio
 *
 * The paratemeter `id` is a string representing the filename to the audio in the media library folder
 * `./sfx/default` directory, without the '.mp3' extension.
 *
 * @param {string} id - the ID of the audio
 **/
async function playSfx(id?: string) {
  if (!isEnabled) {
    return false;
  }

  // if silent, stop here
  if (isSilent()) {
    return true;
  }

  if (!id) {
    throw new Error('No id to play');
  }

  const file = getFile(id);
  const volumne =
    Number(safeLocalStorage.getItem('uc-audio-volume') ?? 1) * 0.3;
  if (!file) return false;
  try {
    if (!isSilent()) {
      file.currentTime = 0;
      file.play();
      file.volume = volumne;
      file.playbackRate = 1;
    }
  } catch (e) {
    console.error(e);
  }
}

export { playSfx, getFile, getSfxPath, preloadSfx, disableSfx, enableSfx };
